/* [ START OF MODULE - VER 1.0 - BLOCK 1 of 1 (src/pages/SetupPassword.jsx) ] */
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient'; // Adjust path if necessary
import logo from '../assets/Logo.png'; // Adjust path if necessary

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  :root{
    --ttl-blue:#5899CD;--ttl-purple:#824AF8;--ttl-coral:#F37664;--ttl-dark:#231F20;
    --ttl-grad:linear-gradient(135deg,#5899CD 0%,#824AF8 55%,#F37664 100%);
  }
  .gate-root{min-height:100vh;background:var(--ttl-dark);display:flex;align-items:center;justify-content:center;font-family:'Poppins',sans-serif;overflow:hidden;position:relative}
  .gate-bg{position:fixed;inset:0;z-index:0}
  .gate-blob{position:absolute;border-radius:50%;filter:blur(90px)}
  .gate-blob--1{width:520px;height:520px;background:var(--ttl-purple);opacity:.17;top:-130px;left:-130px;animation:gDrift 12s ease-in-out infinite alternate}
  .gate-blob--2{width:430px;height:430px;background:var(--ttl-blue);opacity:.15;bottom:-90px;right:-90px;animation:gDrift 16s ease-in-out infinite alternate-reverse}
  .gate-blob--3{width:290px;height:290px;background:var(--ttl-coral);opacity:.09;top:50%;left:50%;animation:gDrift2 10s ease-in-out infinite alternate}
  @keyframes gDrift{from{transform:translate(0,0) scale(1)}to{transform:translate(40px,30px) scale(1.08)}}
  @keyframes gDrift2{from{transform:translate(-50%,-50%) scale(1)}to{transform:translate(-45%,-45%) scale(1.1)}}
  .gate-grid{position:absolute;inset:0;background-image:radial-gradient(rgba(255,255,255,.05) 1px,transparent 1px);background-size:32px 32px}
  .gate-card{position:relative;z-index:1;width:440px;background:rgba(35,31,32,.82);backdrop-filter:blur(28px);-webkit-backdrop-filter:blur(28px);border-radius:20px;overflow:hidden;box-shadow:0 0 0 1px rgba(255,255,255,.07),0 32px 64px rgba(0,0,0,.6);animation:gRise .75s cubic-bezier(.16,1,.3,1) both}
  @keyframes gRise{from{opacity:0;transform:translateY(30px) scale(.96)}to{opacity:1;transform:none}}
  .gate-topbar{height:5px;background:var(--ttl-grad)}
  .gate-body{padding:44px 44px 40px;display:flex;flex-direction:column;align-items:center}
  .gate-logo-wrap{margin-bottom:28px;display:flex;flex-direction:column;align-items:center;gap:12px}
  .gate-logo-ring{width:84px;height:84px;border-radius:50%;padding:3px;background:var(--ttl-grad);animation:gPulse 4s ease-in-out infinite}
  @keyframes gPulse{0%,100%{box-shadow:0 8px 32px rgba(130,74,248,.35)}50%{box-shadow:0 8px 52px rgba(130,74,248,.65)}}
  .gate-logo-ring img{width:100%;height:100%;border-radius:50%;object-fit:cover;display:block}
  .gate-heading{text-align:center;margin-bottom:28px}
  .gate-heading h1{font-size:26px;font-weight:700;color:#fff;line-height:1.2;margin-bottom:4px}
  .gate-heading h2{font-size:15px;font-weight:600;background:var(--ttl-grad);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px}
  .gate-heading p{font-size:13px;color:rgba(255,255,255,.35)}
  .gate-form{width:100%;display:flex;flex-direction:column;margin-bottom:12px;}
  .gate-input{width:100%;padding:12px 14px;background:rgba(0,0,0,0.25);border:1px solid rgba(255,255,255,0.1);border-radius:6px;color:#fff;font-family:'Poppins',sans-serif;font-size:13px;margin-bottom:12px;outline:none;transition:border-color 0.2s;}
  .gate-input:focus{border-color:var(--ttl-purple);}
  .gate-input::placeholder{color:rgba(255,255,255,0.3);}
  .gate-btn{width:100%;padding:12px;background:var(--ttl-grad);border:none;border-radius:6px;color:#fff;font-family:'Poppins',sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:opacity 0.2s;display:flex;justify-content:center;align-items:center;gap:8px;}
  .gate-btn:hover{opacity:0.9;}
  .gate-btn:disabled{opacity:0.5;cursor:not-allowed;}
  .gate-error{color:var(--ttl-coral);font-size:12px;margin-bottom:12px;text-align:center;background:rgba(243,118,100,0.1);padding:8px;border-radius:4px;border:1px solid rgba(243,118,100,0.2);}
  .gate-success{color:var(--accent-success);font-size:13px;font-weight:600;margin-bottom:12px;text-align:center;background:rgba(16,185,129,0.1);padding:12px;border-radius:4px;border:1px solid rgba(16,185,129,0.2);}
  @media(max-width:480px){.gate-card{width:92vw}.gate-body{padding:36px 28px 32px}}
`;

const SetupPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Optional: Verify there is an active session before allowing password setup
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError("Invalid or expired invite link. Please request a new invitation.");
      }
    };
    checkSession();
  }, []);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // This is the critical Supabase function. Because the user arrived via the secure
      // email link, they have a temporary session. This attaches the password to it.
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });
      
      if (updateError) throw updateError;
      
      setSuccess(true);
      
      // Give the user a moment to see the success message, then route them in
      setTimeout(() => {
        window.location.href = '/'; // Routes to your main Command Center dashboard
      }, 2000);
      
    } catch (err) {
      setError(err.message || "Failed to set password. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="gate-root">
        <div className="gate-bg">
          <div className="gate-blob gate-blob--1" />
          <div className="gate-blob gate-blob--2" />
          <div className="gate-blob gate-blob--3" />
          <div className="gate-grid" />
        </div>
        <div className="gate-card">
          <div className="gate-topbar" />
          <div className="gate-body">
            <div className="gate-logo-wrap">
              <div className="gate-logo-ring">
                <img src={logo} alt="Tiger Technology & Logistics" />
              </div>
            </div>
            <div className="gate-heading">
              <h1>Welcome Aboard</h1>
              <h2>Account Activation</h2>
              <p>Please secure your Command Center account by creating a password.</p>
            </div>
            
            {success ? (
              <div className="gate-success">
                Password secured! Redirecting to Command Center...
              </div>
            ) : (
              <form className="gate-form" onSubmit={handleUpdatePassword}>
                {error && <div className="gate-error">{error}</div>}
                
                <input 
                  type="password" 
                  className="gate-input" 
                  placeholder="New Password (min 8 characters)" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading || error?.includes('Invalid')}
                  required
                />
                <input 
                  type="password" 
                  className="gate-input" 
                  placeholder="Confirm New Password" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading || error?.includes('Invalid')}
                  required
                />
                
                <button type="submit" className="gate-btn" disabled={loading || error?.includes('Invalid')}>
                  {loading ? 'Securing Account...' : 'Set Password & Enter'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SetupPassword;

/*
=========================================
CHANGE MANIFEST
=========================================
VER 1.0 - Setup Password Landing Zone
- CORE: Built the password configuration module to catch incoming invite links.
- LOGIC: Implemented `supabase.auth.updateUser` to permanently bind the password to the user's temporary auth token.
- UI/UX: Replicated the high-fidelity `TigerGate` styling to ensure visual continuity during onboarding.
- ROUTING: Added an automatic `window.location.href` redirect to thrust the tester directly into the app upon success.
=========================================
*/
/* [ END OF MODULE - VER 1.0 - BLOCK 1 of 1 (src/pages/SetupPassword.jsx) ] */